/**
 * states.js
 * 
 * This module defines the global state management for the SightFlight Designer application
 * using Recoil. Recoil provides a way to manage shared state across React components
 * with atoms (individual state pieces) and selectors (derived state).
 * 
 * The states defined here manage various aspects of the application including:
 * - Polygon data and selection
 * - Setback configuration
 * - Obstacle management
 * - Solar panel configuration
 */

import { atom, selector } from 'recoil';

/**
 * Main storage for polygon data in the application
 * This is the primary data structure that holds all segment/polygon information necessary for reconstruction
 */
export const polygonsState = atom({
    key: 'polygonsState',
    default: []
});

/**
 * Toggle state for the "magic" feature
 * Controls whether automatic optimization/features are enabled
 */
export const magicToggleState = atom({
    key: 'magicToggleState',
    default: false
});

/**
 * Stores input values for setback distances
 * Maps edge indices to their corresponding setback values
 */
export const setBackInput = atom({
    key: 'setBackInput',
    default: {}
});

/**
 * Stores the available area calculation result
 * Typically shows the square footage of the area after setbacks are applied
 */
export const avaArea = atom({
    key: 'avaArea',
    default: ''
});

/**
 * Tracks the currently selected indices for segment, obstacle, and panel
 * Used to determine which elements are being edited or viewed
 */
export const selectedIndicesState = atom({
    key: 'selectedIndicesState',
    default: { segmentIndex: null, obstacleIndex: null, panelIndex: null }
});

/**
 * Stores the index of a segment that should be deleted
 * Set when a deletion is requested, then processed by a component that handles deletion
 */
export const deleteSegmentIndex = atom({
    key: 'deleteSegmentIndex',
    default: null
});

/**
 * Stores the index of an obstacle that should be deleted
 * Set when a deletion is requested, then processed by a component that handles deletion
 */
export const deleteObstacleIndex = atom({
    key: 'deleteObstacleIndex',
    default: null
});

/**
 * Stores the index of a solar panel that should be deleted
 * Set when a deletion is requested, then processed by a component that handles deletion
 */
export const deletePanelIndex = atom({
    key: 'deletePanelIndex',
    default: null
});

/**
 * Stores the height value for the currently selected obstacle
 * Used in the UI to display and modify obstacle height
 */
export const selectedObstacleHeight = atom({
    key: 'selectedObstacleHeight',
    default: 0,
});

/**
 * Stores the offset value for the currently selected obstacle
 * Determines positioning of the obstacle relative to its base location
 */
export const selectedObstacleOffset = atom({
    key: 'selectedObstacleOffset',
    default: 6
});

/**
 * Toggle state for the panel settings UI
 * Controls whether the panel configuration interface is visible
 */
export const panelSettings = atom({
    key: 'panelSettings',
    default: false
});

/**
 * Overlay notice displayed above the design canvas
 * Useful for transient status messages tied to drawing actions.
 */
export const canvasNoticeState = atom({
    key: 'canvasNoticeState',
    default: {
        visible: false,
        title: '',
        subtitle: '',
        intent: 'info',
        source: null,
    },
});

// Progress from the long-running Dronify DSM/PLY fallback. Kept separate from
// canvas notices because this job has no deterministic completion percentage.
export const lidarLoaderState = atom({
    key: 'lidarLoaderState',
    default: {
        visible: false,
        title: '',
        stageLabel: '',
        startedAt: null,
        progress: 0,
    },
});

/**
 * Stores configuration for the currently selected solar panel
 * Includes panel ID, orientation (portrait/landscape), and height
 */
export const solarPanel = atom({
    key: 'solarPanel',
    default: { id: null, orientation: "Portrait", height: 0, tilt: 0, rotation: 0, azimuth: 0 }
});

/**
 * Flag indicating if the current panel layout is valid
 * Used to show warnings or prevent saving invalid configurations
 */
export const panelsOK = atom({
    key: 'panelsOK',
    default: true
});

/**
 * Selector that derives polygon details for the currently selected segment
 * This is a computed value based on the selected segment index and polygons data
 */
export const polygonDetailsSelector = selector({
    key: 'polygonDetailsSelector',
    get: ({ get }) => {
        // Get the current selected indices and polygons data
        const selectedIndices = get(selectedIndicesState);
        const polygons = get(polygonsState);
        
        // Return the details for the selected segment, or null if none is selected
        return selectedIndices.segmentIndex !== null ? 
            polygons[selectedIndices.segmentIndex].details : null;
    }
});
